/*
 * DelayTracker — passive, continuous echo round-trip delay estimator.
 *
 * Purpose: remove the user-facing one-shot calibration.  Instead of playing a
 * clip on the prejoin screen and measuring once, this watches the live
 * (mic, far) 16 kHz Int16 pairs that sync-aec-worklet already produces and,
 * whenever the far-end (remote speech) is active and the estimate is
 * confident, updates the NKF far-delay via a callback.
 *
 * This mirrors what WebRTC AEC3 does with a continuously-running delay
 * controller, so (a) no calibration step is needed and (b) the delay tracks
 * drift throughout the call — Bluetooth latency changes, mic/output clock
 * skew (SRO), and device switches.
 *
 * Cost: GCC-PHAT runs on a 4× decimated (16k→4k) window, so the FFT is small
 * (~8k pts) and runs off the caller's message handler via a microtask, one at
 * a time.  Delay resolution stays 4 samples @16k (0.25 ms) — far finer than
 * NKF needs (L=4 → 64 ms window).
 *
 * Discipline borrowed from AEC3 (all tunable via constructor opts):
 *   - far-energy gate    : need remote speech to align against
 *   - confidence gate    : peakFloorRatio; double-talk / noise drop it, so bad
 *                          frames are ignored automatically
 *   - stability gate     : commit the median only once N confident estimates
 *                          agree within a tight spread
 *   - hysteresis         : only move the delay when it changed meaningfully
 *
 * Android warm-up (measured on-device via /nkf-loop/): the physical echo path
 * delay RAMPS during the first ~30 s after the audio pipeline starts
 * (e.g. 114 → 158 → 222 ms as the HAL regrows its buffers), and the settled
 * value differs run-to-run, so neither a seed nor a one-shot calibration can
 * cover it.  The 3-hit stability gate needed ~27 s to follow the ramp — all
 * leaked echo, which is what let the AI hear itself and loop.  Committing a
 * single high-confidence estimate immediately closed that window to ~2 s with
 * no mis-tracking.  Hence two extra paths:
 *   - warm-up phase      : for warmupMs after construction (or rearm()), one
 *                          extra-confident estimate commits immediately
 *   - jump detection     : after warm-up, jumpHits consecutive confident
 *                          estimates that agree with each other but sit far
 *                          from the current delay commit without waiting for
 *                          the slow gate (device/route switches mid-call)
 */

import { gccPhat } from './calibrator.js';

const SR = 16000;

export class DelayTracker {
    constructor({
        onDelay,
        initialDelay    = 0,
        windowSec       = 1.0,   // analysis window
        decimation      = 4,     // 16k → 4k for a cheap FFT
        measureEveryMs  = 500,   // how often to attempt a measurement
        maxLagMs        = 400,   // round-trip search range (0..maxLag)
        minPeakRatio    = 8,     // GCC-PHAT peak/floor confidence threshold
        stableHits      = 3,     // consecutive confident estimates required
        stableTolSamples= 96,    // 6 ms — max spread across those estimates
        minStepSamples  = 64,    // 4 ms — hysteresis; ignore smaller moves
        farEnergyMin    = 0.004, // mean |far| below this = remote silent → skip
        warmupMs        = 60000, // fast-follow window after construction/rearm
        warmupMinPeak   = 10,    // higher bar for the 1-hit warm-up commit
        jumpTolSamples  = 384,   // 24 ms — deviation that counts as a "jump"
        jumpHits        = 2,     // consecutive agreeing jump estimates to commit
    } = {}) {
        this._onDelay    = onDelay;
        this._current    = initialDelay | 0;
        this._win        = Math.max(2048, Math.round(windowSec * SR));
        this._ds         = Math.max(1, decimation | 0);
        this._measureMs  = measureEveryMs;
        this._maxLag     = Math.round(maxLagMs * SR / 1000);
        this._minPeak    = minPeakRatio;
        this._stableHits = stableHits;
        this._stableTol  = stableTolSamples;
        this._minStep    = minStepSamples;
        this._farEnergyMin = farEnergyMin;
        this._warmupMs   = warmupMs;
        this._warmupMinPeak = warmupMinPeak;
        this._jumpTol    = jumpTolSamples;
        this._jumpHits   = jumpHits;
        this._warmupUntil = _nowMs() + warmupMs;
        this._jumpBuf    = [];   // consecutive confident lags far from current

        this._mic    = new Float64Array(this._win);   // ring buffers (16 kHz)
        this._far    = new Float64Array(this._win);
        this._pos    = 0;     // ring write index
        this._filled = 0;     // samples written (caps at _win)
        this._lastMeasureMs = 0;
        this._busy   = false;
        this._recent = [];    // recent confident lags (16k samples) for stability
        this._stopped = false;
    }

    /** Feed one worklet chunk pair (Int16Array @16 kHz).  Cheap: just copies
     *  into the ring; heavy FFT work is deferred to a microtask. */
    push(micI16, farI16) {
        if (this._stopped || !micI16 || !farI16) return;
        const n = Math.min(micI16.length, farI16.length);
        const win = this._win, mic = this._mic, far = this._far;
        let p = this._pos;
        for (let i = 0; i < n; i++) {
            mic[p] = micI16[i] / 32768;
            far[p] = farI16[i] / 32768;
            if (++p >= win) p = 0;
        }
        this._pos = p;
        this._filled = Math.min(win, this._filled + n);

        const now = _nowMs();
        if (this._filled >= win && !this._busy && (now - this._lastMeasureMs) >= this._measureMs) {
            this._lastMeasureMs = now;
            this._busy = true;
            _defer(() => { try { this._measure(); } finally { this._busy = false; } });
        }
    }

    _measure() {
        if (this._stopped) return;
        const win = this._win, ds = this._ds, pos = this._pos;
        const m = Math.floor(win / ds);
        const micD = new Float64Array(m);
        const farD = new Float64Array(m);

        // Linearize ring (oldest → newest) and box-average decimate by `ds`.
        let farAbs = 0;
        for (let i = 0; i < m; i++) {
            let sm = 0, sf = 0;
            for (let d = 0; d < ds; d++) {
                const src = i * ds + d;
                let k = pos + src; if (k >= win) k -= win;   // oldest starts at pos
                sm += this._mic[k];
                sf += this._far[k];
            }
            micD[i] = sm / ds;
            farD[i] = sf / ds;
            farAbs += Math.abs(farD[i]);
        }
        farAbs /= m;
        if (farAbs < this._farEnergyMin) return;             // remote silent → skip

        const maxLagD = Math.min(Math.round(this._maxLag / ds), m - 1);
        const { lag, peakFloorRatio } = gccPhat(farD, micD, maxLagD);
        if (!(peakFloorRatio >= this._minPeak)) return;      // low confidence (noise/double-talk)

        const lag16 = lag * ds;
        const dev = Math.abs(lag16 - this._current);

        // Warm-up fast path: the Android echo path ramps for ~30 s after the
        // pipeline starts, so waiting for the stability gate leaks echo the
        // whole time.  One extra-confident estimate commits immediately.
        if (_nowMs() < this._warmupUntil
                && peakFloorRatio >= this._warmupMinPeak
                && dev > this._minStep) {
            this._commit(lag16, peakFloorRatio, 'warmup');
            return;
        }

        // Jump path: a route/device switch mid-call moves the delay far and
        // fast.  jumpHits consecutive confident estimates that agree with each
        // other but sit far from the current delay commit right away.
        if (dev > this._jumpTol) {
            this._jumpBuf.push(lag16);
            if (this._jumpBuf.length > this._jumpHits) this._jumpBuf.shift();
            if (this._jumpBuf.length >= this._jumpHits) {
                const js = [ ...this._jumpBuf ].sort((a, b) => a - b);
                if (js[js.length - 1] - js[0] <= this._stableTol) {
                    this._commit(js[js.length >> 1], peakFloorRatio, 'jump');
                    return;
                }
            }
        } else {
            this._jumpBuf.length = 0;   // deviation must be consecutive
        }

        // Stability: median of the last N confident lags, committed only when
        // their spread is tight (settled), then hysteresis on the change.
        this._recent.push(lag16);
        if (this._recent.length > this._stableHits) this._recent.shift();
        if (this._recent.length < this._stableHits) return;
        const sorted = [ ...this._recent ].sort((a, b) => a - b);
        if (sorted[sorted.length - 1] - sorted[0] > this._stableTol) return;   // not settled
        const median = sorted[sorted.length >> 1];

        if (Math.abs(median - this._current) > this._minStep) {
            this._commit(median, peakFloorRatio, 'drift');
        }
    }

    _commit(lag16, peakFloorRatio, phase) {
        this._current = lag16;
        this._jumpBuf.length = 0;
        this._recent.length = 0;
        try {
            this._onDelay?.(lag16, { peakFloorRatio, delayMs: lag16 * 1000 / SR, phase });
        } catch { /* ignore */ }
    }

    /** Re-enters the warm-up fast-follow window.  Call when the output path
     *  likely changed (outputLatency moved, devicechange) so the tracker
     *  re-converges in seconds instead of tens of seconds. */
    rearm() {
        this._warmupUntil = _nowMs() + this._warmupMs;
        this._recent = [];
        this._jumpBuf.length = 0;
    }

    stop() {
        this._stopped = true;
        this._mic = this._far = null;
        this._recent = [];
    }
}

function _nowMs() {
    return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
}

function _defer(fn) {
    if (typeof queueMicrotask === 'function') queueMicrotask(fn);
    else Promise.resolve().then(fn);
}
